import { Capacitor } from "@capacitor/core";
import { Share as CapacitorShare } from "@capacitor/share";
import { useMutation } from "@tanstack/react-query";

import { Models } from "@/api/types";
import { generateReferralShareText } from "@/modules/referralRewards";
import { queryClient } from "@/pages/Root";
import { get, post } from "api";

type UseShareReferralRewardParams = {
  platform: Models<"PlatformModel">;
  user: Models<"UserModel1">;
  rewardTitle: string;
};

type UseShareReferralRewardResult = {
  shareReferralReward: () => Promise<string | null>;
};

export const useShareReferralReward = ({
  platform,
  user,
  rewardTitle,
}: UseShareReferralRewardParams): UseShareReferralRewardResult => {
  const referralMutation = useMutation({
    mutationFn: async (personId: string) => {
      await post("/referral-codes/personal-code/{personId}", {
        personId,
      });

      const codes = await get(
        "/referral-codes/personal-code/{personId}/share-invite",
        {
          personId,
        }
      );

      const currentPlatformCode = codes.find(
        (code: Models<"PlatformShareInvite">) =>
          code.platformId.value === platform.id
      );

      if (!currentPlatformCode) {
        throw new Error("Current platform code not found");
      }

      const statelessShareablePrettyToken = await post(
        "/referral-codes/stateless-share-url/{personalReferralCode}/platform/{platformShareInvite}",
        {
          personalReferralCode:
            currentPlatformCode.personalReferralCode.rawValue,
          platformShareInvite: currentPlatformCode.rawValue,
        }
      );

      const trackedShareUrl = (await get(
        "/referral-codes/stateless-share-url/{statelessPrettyToken}",
        {
          statelessPrettyToken: statelessShareablePrettyToken,
        }
      )) as Models<"DisplayAnonymousStatelessShareUrl">;

      return trackedShareUrl.shortenedUrl ?? trackedShareUrl.absoluteUrl;
    },
    onError: (error) => {
      console.error("Error creating referral share text:", error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries([
        "/incentives/{platformId}/referral-history/{personId}",
        platform.id,
        user.id,
      ]);

      queryClient.refetchQueries([
        "/incentives/{platformId}/referral-history/{personId}",
        platform.id,
        user.id,
      ]);
    },
  });

  const share = async (urlToShare: string) => {
    const shareText = generateReferralShareText({
      platformName: platform.name ?? "",
      rewardTitle,
    });

    if (Capacitor.isNativePlatform()) {
      try {
        await CapacitorShare.share({
          text: `${shareText.trim()} ${urlToShare}`,
        });
      } catch (error) {
        console.error("Error sharing with Capacitor:", error);
      }
      return;
    }

    if (navigator.share) {
      try {
        await navigator.share({
          text: `${shareText.trim()} ${urlToShare}`,
        });
      } catch (error) {
        console.error("Error sharing with navigator.share:", error);
      }
      return;
    }

    // Fallback to email for Windows and other platforms without share support
    const emailBody = `${shareText}\n\n${urlToShare}`;
    window.location.href = `mailto:?body=${encodeURIComponent(emailBody)}`;
    console.log(`Fallback email share. Referral URL: ${urlToShare}`);
  };

  const shareReferralReward = async (): Promise<string | null> => {
    try {
      const referralUrl = await referralMutation.mutateAsync(user.id);

      const urlWithLocation = `${referralUrl}`;

      await share(urlWithLocation);
      return urlWithLocation;
    } catch (error) {
      console.error("Error in shareReferralReward:", error);
      return null;
    }
  };

  return {
    shareReferralReward,
  };
};
